<template>
  <header
    :class="{ scroll: isScrolled || isHovered }"
    @mouseenter="isHovered = true"
    @mouseleave="handleHeaderMouseLeave"
  >
    <div class="container">
      <div class="navbar">
        <div class="logo">
          <router-link to="/">
            <img :src="logo" v-if="isScrolled || isHovered" alt="源林投资" />
            <img :src="logoWhite" v-else alt="源林投资" />
          </router-link>
        </div>
        <div class="mobile-menu-toggle" @click="toggleMobileMenu">
          <i class="pi" :class="mobileMenuOpen ? 'pi-times' : 'pi-bars'"></i>
        </div>
        <ul class="nav-menu" :class="{ active: mobileMenuOpen }">
          <li>
            <router-link
              to="/"
              :class="navLinkClass(0)"
              @mouseover="hoveredIndex = 0"
              @mouseleave="hoveredIndex = null"
            >{{ $t('nav.home') }}</router-link>
          </li>
          <li
            class="dropdown"
            @mouseenter="activeDropdown = 'about'"
            @mouseleave="handleDropdownMouseLeave"
          >
            <a
              href="#"
              :class="navLinkClass(1)"
              @mouseover="hoveredIndex = 1"
              @mouseleave="hoveredIndex = null"
            >{{ $t('nav.about') }}</a>
          </li>
          <li
            class="dropdown"
            @mouseenter="activeDropdown = 'projects'"
            @mouseleave="handleDropdownMouseLeave"
          >
            <a
              href="#"
              :class="navLinkClass(2)"
              @mouseover="hoveredIndex = 2"
              @mouseleave="hoveredIndex = null"
            >{{ $t('nav.projects') }}</a>
          </li>
          <li>
            <router-link
              to="/news"
              :class="navLinkClass(3)"
              @mouseover="hoveredIndex = 3"
              @mouseleave="hoveredIndex = null"
            >{{ $t('nav.news') }}</router-link>
          </li>
          <li>
            <router-link
              to="/charity"
              :class="navLinkClass(4)"
              @mouseover="hoveredIndex = 4"
              @mouseleave="hoveredIndex = null"
            >{{ $t('nav.charity') }}</router-link>
          </li>
          <li
            class="dropdown"
            @mouseenter="activeDropdown = 'contact'"
            @mouseleave="handleDropdownMouseLeave"
          >
            <a
              href="#"
              :class="navLinkClass(5)"
              @mouseover="hoveredIndex = 5"
              @mouseleave="hoveredIndex = null"
            >{{ $t('nav.contact') }}</a>
          </li>
        </ul>
        <div class="language-switch">
          <span
            class="lang-btn"
            @click="changeLanguage('zh')"
            :class="{ active: currentLocale === 'zh' }"
            >中</span
          >
          <span class="lang-divider">|</span>
          <span
            class="lang-btn"
            @click="changeLanguage('en')"
            :class="{ active: currentLocale === 'en' }"
            >EN</span
          >
        </div>
      </div>
    </div>
  </header>

  <!-- 拆离的二级菜单 -->
  <div
    v-if="activeDropdown"
    class="dropdown-bar"
    @mouseenter="dropdownHover = true"
    @mouseleave="dropdownHover = false; isHovered = false; activeDropdown = ''"
  >
    <div class="dropdown-content container">
      <template v-if="activeDropdown === 'about'">
        <router-link to="/introduction">{{ $t("nav.company") }}</router-link>
        <router-link to="/history">{{ $t("nav.history") }}</router-link>
        <router-link to="/leader">{{ $t("nav.leader") }}</router-link>
        <router-link to="/honors">{{ $t("nav.honors") }}</router-link>
      </template>
      <template v-else-if="activeDropdown === 'projects'">
        <router-link to="/chuxin-restaurant">{{ $t("nav.chuxin") }}</router-link>
        <router-link to="/commercial-building">{{ $t("nav.hotel") }}</router-link>
        <router-link to="/ecological-park">{{ $t("nav.ecopark") }}</router-link>
        <router-link to="/agriculture">{{ $t("nav.agriculture") }}</router-link>
        <router-link to="/food-projects">{{ $t("nav.food") }}</router-link>
        <router-link to="/industrial-park">{{ $t("nav.industrial") }}</router-link>
        <router-link to="/comprehensive-building">{{ $t("nav.building") }}</router-link>
        <router-link to="/yuanlin">{{ $t("nav.yuanlin") }}</router-link>
      </template>
      <template v-else-if="activeDropdown === 'contact'">
        <router-link to="/contact">{{ $t("nav.contact") }}</router-link>
        <!-- <router-link to="/message">{{ $t("nav.message") }}</router-link> -->
        <router-link to="/jobs">{{ $t("nav.jobs") }}</router-link>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import logoImg from "@/assets/logo.png";
import logoImgWhite from "@/assets/logoWhite.png";

export default {
  name: "HeaderComponent",
  setup() {
    const { locale } = useI18n();

    const isScrolled = ref(false);
    const isHovered = ref(false);
    const mobileMenuOpen = ref(false);
    const activeDropdown = ref("");
    const dropdownHover = ref(false);
    const logo = ref(logoImg);
    const logoWhite = ref(logoImgWhite);
    const hoveredIndex = ref(null);

    const navLinkClass = (idx) => {
      if (isScrolled.value || isHovered.value) {
        return hoveredIndex.value === idx ? 'hovered' : 'scrolled';
      } else {
        return hoveredIndex.value === idx ? 'hovered' : '';
      }
    };

    const currentLocale = computed(() => locale.value);

    const handleScroll = () => {
      isScrolled.value = window.scrollY > 50;
    };

    const toggleMobileMenu = () => {
      mobileMenuOpen.value = !mobileMenuOpen.value;
      if (mobileMenuOpen.value) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "auto";
      }
    };

    const closeMobileMenu = () => {
      mobileMenuOpen.value = false;
      document.body.style.overflow = "auto";
    };

    const toggleDropdown = (dropdown) => {
      if (window.innerWidth <= 991) {
        activeDropdown.value =
          activeDropdown.value === dropdown ? "" : dropdown;
      }
    };

    const changeLanguage = (lang) => {
      locale.value = lang;
    };

    const handleDropdownMouseLeave = () => {
      if (!dropdownHover.value) activeDropdown.value = '';
    };

    const handleHeaderMouseLeave = () => {
      if (!dropdownHover.value) {
        isHovered.value = false;
      }
    };

    onMounted(() => {
      window.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", () => {
        if (window.innerWidth > 991) {
          mobileMenuOpen.value = false;
          document.body.style.overflow = "auto";
        }
      });
    });

    return {
      isScrolled,
      isHovered,
      mobileMenuOpen,
      activeDropdown,
      dropdownHover,
      logo,
      logoWhite,
      currentLocale,
      toggleMobileMenu,
      closeMobileMenu,
      toggleDropdown,
      changeLanguage,
      handleDropdownMouseLeave,
      handleHeaderMouseLeave,
      hoveredIndex,
      navLinkClass,
    };
  },
};
</script>

<style scoped>
header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  background: transparent;
  transition: background 0.3s, box-shadow 0.3s;
  height: 100px;
}

.nav-menu {
  /* height: 100%; */
  height: 100px;
  margin-bottom: 0;
  display: flex;
  align-items: center;
}

.nav-menu > li {
  height: 100px;
  line-height: 100px;
  display: flex;
  align-items: center;
}

.nav-menu > li > a {
  position: relative;
  color: #fff;
  transition: color 0.3s;
  font-size: 16px;
  font-weight: normal;
  letter-spacing: 0.5px;
  height: 100%;
  line-height: 100px;
  display: flex;
  align-items: center;
}

header .nav-menu > li > a {
  line-height: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  position: relative;
  color: #fff !important;
  transition: color 0.3s;
  font-size: 16px;
  font-weight: normal;
}

header.scroll {
  background: #fff;
  box-shadow: 0 2px 10px 2px rgba(0, 0, 0, 0.2);
}

header.scroll .nav-menu > li > a {
  color: #333 !important;
  font-weight: 400;
}

header .nav-menu > li > a:hover {
  color: #333 !important;
}

.container {
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 24px;
  height: 100%;
  box-sizing: border-box;
}

.navbar .nav-menu > li > a,
.navbar .nav-menu > li > .router-link-active,
.navbar .nav-menu > li > .router-link-exact-active {
  color: inherit;
  transition: color 0.3s;
}

.navbar .nav-menu > li.active > a,
.navbar .nav-menu > li.active > .router-link-active {
  color: #c9a063;
}

.navbar .nav-menu > li > a::after {
  content: '';
  display: block;
  position: absolute;
  left: 50%;
  bottom: -4px;
  width: 0;
  height: 2px;
  background: #c9a063;
  border-radius: 1px;
  transition: width 0.3s, left 0.3s;
}

.nav-menu > li > a:hover::after {
  width: 60%;
  left: 20%;
}

.navbar .dropdown-menu {
  display: none;
  position: absolute;
  left: 0;
  width: 100%;
  background: transparent;
  border: none;
  box-shadow: none;
  padding: 0;
  margin: 0;
  z-index: 1000;
}

.navbar .dropdown-menu.show {
  display: flex;
  justify-content: center;
  background: transparent;
}

.navbar .dropdown-menu > .container {
  background: #23408e;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: 0;
  min-height: 48px;
  align-items: center;
}

.navbar .dropdown-menu li {
  display: inline-block;
}

.navbar .dropdown-menu li a {
  color: #fff;
  padding: 12px 32px;
  display: block;
  transition: background 0.2s;
  white-space: nowrap;
  font-size: 16px;
}

.navbar .dropdown-menu li a:hover {
  background: #c9a063;
  color: #fff;
}

.close-mobile-menu {
  display: none;
}

.mobile-lang {
  display: none;
}

@media (max-width: 991px) {
  .close-mobile-menu {
    display: block;
    position: absolute;
    top: 20px;
    right: 20px;
    font-size: 24px;
    cursor: pointer;
  }

  .mobile-lang {
    display: block;
    margin-top: 20px;
    text-align: center;
  }

  .language-switch {
    display: none;
  }

  .navbar .dropdown-menu,
  .navbar .dropdown-menu.show {
    display: block;
    position: static;
    width: 100%;
    background: #23408e;
    border-radius: 0;
    box-shadow: none;
  }

  .navbar .dropdown-menu > .container {
    flex-direction: column;
    min-height: unset;
    padding: 0;
  }

  .navbar .dropdown-menu li a {
    padding: 12px 24px;
  }

  header {
    height: 100px;
  }
}

.dropdown-menu {
  z-index: 1000;
}

.language-switch {
  display: flex;
  align-items: center;
  gap: 8px;
}

.lang-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f5f5f5;
  color: #23408e;
  font-weight: bold;
  font-size: 15px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: background 0.2s, color 0.2s, border 0.2s;
  user-select: none;
}

.lang-btn.active {
  background: #23408e;
  color: #fff;
  border: 2px solid #c9a063;
}

.lang-btn:hover {
  background: #c9a063;
  color: #fff;
}

.lang-divider {
  color: #bbb;
  font-size: 16px;
  user-select: none;
}

.dropdown-bar {
  position: fixed;
  top: 99px;
  left: 0;
  width: 100%;
  background: #23408e;
  z-index: 101;
  transition: opacity 0.2s;
}

.dropdown-content.container {
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 24px;
  height: 56px;
  gap: 0;
}

.dropdown-content a {
  color: #fff !important;
  background: transparent;
  font-size: 16px;
  font-weight: normal;
  padding: 0 24px;
  text-decoration: none;
  transition: color 0.2s, background 0.2s;
  line-height: 56px;
  position: relative;
  letter-spacing: 0.5px;
  border-radius: 0;
  white-space: nowrap;
  margin: 0 8px;
  flex: 0 1 auto;
  box-sizing: border-box;
}

.dropdown-content a:hover,
.dropdown-content .router-link-active,
.dropdown-content .router-link-exact-active {
  color: #fff !important;
  background: #c9a063;
  border-radius: 0;
}

.nav-menu > li > a.scrolled {
  color: #333;
}

.nav-menu > li > a.hovered {
  color: #c9a063 !important;
}

.nav-menu > li > a::after {
  content: '';
  display: block;
  position: absolute;
  left: 50%;
  bottom: -4px;
  width: 0;
  height: 2px;
  background: #c9a063;
  border-radius: 1px;
  transition: width 0.3s, left 0.3s;
}

.nav-menu > li > a.hovered::after {
  width: 60%;
  left: 20%;
}
</style>
